(() => {
  const HELP_FORM_MESSAGES = {
    billing: "I need help with a billing question",
    contact_us: "I'd like to talk with the Armadillo Tough team",
    design_studio: "I'd like a free design consultation",
    installation: "I'd like to request installation help",
    product_inquiry: "I need help with an Armadillo Tough product",
    refunds: "I'd like to start a return",
    retail_partners: "I'd like to become an Armadillo Tough reseller",
    trade_partners: "I'd like to become an Armadillo Tough reseller",
    track_my_order: "I'd like to track my order",
    warranty_claim: "I'd like to file a warranty claim"
  };

  const SUPPORT_MESSAGES = {
    'channel-partners-resellers': "I'd like to become an Armadillo Tough reseller",
    contact: "I'd like to talk with the Armadillo Tough team",
    'product-registration': "I'd like to register my Armadillo Tough product",
    'product-support': "I need help with an Armadillo Tough product",
    'track-my-order': "I'd like to track my order"
  };

  const normalizeFormName = (value) => (value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');

  const messageForLink = (link) => {
    const explicitMessage = link.dataset.interactorMessage;
    if (explicitMessage) return explicitMessage;

    if (link.tagName !== 'A') return '';

    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.origin);
    } catch (error) {
      return '';
    }

    if (url.hostname === 'help.armadillotough.com') {
      const label = link.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      const context = link.closest('p, li')?.textContent.replace(/\s+/g, ' ').trim().toLowerCase() || label;

      if (/register.*product|product registration/.test(label)) {
        return "I'd like to register my Armadillo Tough product";
      }

      const formName = normalizeFormName(url.searchParams.get('ticket_form'));

      if (formName === 'contact_us') {
        if (/spare part|replacement part|doors separately|wheels separately/.test(context)) {
          return "I need help getting a replacement or spare part";
        }

        if (/installer|installation/.test(context)) {
          return "I need help with an installation issue";
        }

        if (/price match/.test(context)) {
          return "I have a price-match question";
        }
      }

      return HELP_FORM_MESSAGES[formName] || "I need help from the Armadillo Tough team";
    }

    const path = url.pathname.replace(/\/$/, '').toLowerCase();
    const hash = url.hash.slice(1).toLowerCase();

    if (path === '/pages/support' && SUPPORT_MESSAGES[hash]) return SUPPORT_MESSAGES[hash];

    return '';
  };

  // Routing must preserve link contents and layout. Dedicated buttons render
  // their own icons in Liquid; prose and navigation links remain plain text.
  const decorate = (element) => {
    const openOnly = element.hasAttribute('data-interactor-open-only');
    const message = messageForLink(element);
    if (!message && !openOnly) return;

    if (message) element.dataset.interactorMessage = message;
    element.classList.add('interactor-workflow-link');
    element.removeAttribute('target');
    element.removeAttribute('rel');

    const existingLabel = (
      element.getAttribute('aria-label') || element.textContent.replace(/\s+/g, ' ').trim()
    ).replace(/[→↗↓]/g, '').trim();
    if (existingLabel && !/opens support chat/i.test(existingLabel)) {
      element.setAttribute('aria-label', `${existingLabel} - opens support chat`);
    }
  };

  const decorateAll = (root = document) => {
    if (root instanceof Element && root.matches('a[href], [data-interactor-message], [data-interactor-open-only]')) {
      decorate(root);
    }

    root.querySelectorAll?.('a[href], [data-interactor-message], [data-interactor-open-only]').forEach(decorate);
  };

  const getStatusRegion = () => {
    let status = document.querySelector('[data-interactor-workflow-status]');
    if (status) return status;

    status = document.createElement('p');
    status.className = 'interactor-workflow-status';
    status.dataset.interactorWorkflowStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    document.body.appendChild(status);
    return status;
  };

  const findInteractorButton = () => {
    const embedHost = document.querySelector('#interactor-embed-chat');
    const shadowButton = embedHost?.shadowRoot?.querySelector('#interactor-fab');
    return shadowButton || document.querySelector('#interactor-fab');
  };

  const waitForInteractor = ({ requireMessageApi = true } = {}) => new Promise((resolve, reject) => {
    let attempts = 0;

    const check = () => {
      const button = findInteractorButton();
      const messageApi = window.interactor?.message;

      if (button && (!requireMessageApi || typeof messageApi?.send === 'function')) {
        resolve({ button, messageApi });
        return;
      }

      attempts += 1;
      if (attempts < 40) {
        window.setTimeout(check, 250);
        return;
      }

      reject(new Error('Support chat is still loading'));
    };

    check();
  });

  const openWorkflow = async (trigger) => {
    const openOnly = trigger.hasAttribute('data-interactor-open-only');
    const message = trigger.dataset.interactorMessage;
    if ((!message && !openOnly) || trigger.getAttribute('aria-busy') === 'true') return;

    const status = getStatusRegion();
    trigger.setAttribute('aria-busy', 'true');
    status.textContent = 'Opening support chat.';

    try {
      const { button, messageApi } = await waitForInteractor({ requireMessageApi: !openOnly });
      button.click();

      if (!openOnly && trigger.dataset.interactorSent !== message) {
        await messageApi.send(message);
        trigger.dataset.interactorSent = message;
      }

      status.textContent = openOnly ? 'Support chat opened.' : 'Support chat opened with your request.';
      document.dispatchEvent(new CustomEvent('interactor:workflow-opened', {
        detail: { message: message || null, sent: !openOnly }
      }));
    } catch (error) {
      status.textContent = 'Support chat is still loading. Please try again.';
      document.dispatchEvent(new CustomEvent('interactor:workflow-error', { detail: { message } }));
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const trigger = event.target.closest('[data-interactor-message], [data-interactor-open-only]');
    if (!trigger) return;

    event.preventDefault();
    openWorkflow(trigger);
  }, true);

  const initialize = () => {
    decorateAll();

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === 'attributes') {
          decorate(record.target);
          return;
        }

        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) decorateAll(node);
        });
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['href'],
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
